/**
 * Where a notification should actually take you.
 *
 * The backend writes one deep link per notification, and for finance events that link is a
 * `/finance/*` route. But several of those notifications are deliberately addressed to the
 * SALES account owner as well as the Finance team — `build_invoice_overdue` in ai-py sends to
 * `accountOwnerId` on purpose, because the person who owns the customer should know their
 * customer is overdue.
 *
 * That recipient has no `finance:access`, so the link dead-ended: a toast saying the screen
 * exists but isn't theirs. On `freighai_testco` that was 1,643 notifications across 12 users.
 *
 * This maps a finance link to the equivalent place a salesperson CAN open. It is a routing
 * concern only — no permission is granted or widened here, and a user with finance access is
 * never redirected.
 *
 * Kiko Leads #1.
 */
import type { NotificationItem } from './types'

/**
 * The queue renders from the same component on both sides — only the default filter differs.
 * `module=ALL` matters: the row that triggered the notification is a FINANCE follow-up, and the
 * sales route's own default would filter it out and announce "you're all caught up" over it.
 */
const SALES_FOLLOW_UPS = '/sales/schedule?view=todo&module=ALL'

/**
 * Only a real customer id belongs in the customer drawer. Finance invoices are also raised
 * against partners (`prov_…`), which that drawer cannot open — 1,765 overdue invoices on
 * testco are partner-side. Those carry no sales account owner today, so no such notification
 * is sent, but nothing upstream enforces that and a wrong id would open a broken drawer.
 */
function isCustomerId(id: string): boolean {
  return id.startsWith('cust_')
}

export interface NotificationDestination {
  /** Where to navigate. Null means we could not find anywhere useful — tell the user instead. */
  path: string | null
  /** Shown when `path` is null. */
  message?: string
  /**
   * Set when the destination is knowable but needs a lookup first: the notification is about
   * an invoice and carries no `customerId`, so the caller should resolve the invoice's party
   * and route to that customer. Only the ~115 notifications written before `customerId`
   * existed take this path; new ones resolve synchronously.
   */
  resolveCustomerFromInvoiceId?: string
}

/**
 * Where to send someone to look at a customer's invoices.
 *
 * `/sales/customers` is itself gated, on `sales:access`. Not every recipient of a finance
 * notification is in sales — on testco, 59 of them go to pricing leads and ops coordinators
 * who hold neither permission. Sending those to the sales page would just swap one
 * "unauthorised" screen for another, so they get the ungated Operations customer list, which
 * hosts the same drawer and the same Invoices tab.
 *
 * Shared by the sync and the lookup paths so the two cannot drift.
 */
export function customerInvoicesPath(customerId: string, hasSalesAccess: boolean): string {
  const base = hasSalesAccess ? '/sales/customers' : '/customers'
  return `${base}?selected=${encodeURIComponent(customerId)}&tab=invoices`
}

/**
 * The company a notification concerns, if we can tell.
 *
 * `customerId` is the reliable answer but only exists on notifications written after it was
 * added, so fall back to `entityId` when the entity IS the company — that covers the
 * CREDIT_LIMIT_EXCEEDED backlog, whose entityId is already a bare `cust_…`.
 */
export function customerIdOf(n: Pick<NotificationItem, 'customerId' | 'entityType' | 'entityId'>): string | null {
  if (n.customerId && isCustomerId(n.customerId)) return n.customerId
  if (n.entityType === 'COMPANY' && n.entityId && isCustomerId(n.entityId)) return n.entityId
  return null
}

export interface NotificationAccess {
  /** Holders always get the link exactly as written — this is a no-op for the Finance team. */
  finance: boolean
  /** Decides which customer list we can send a non-finance recipient to. */
  sales: boolean
}

/**
 * Resolve the destination for a notification click.
 *
 * Finance users always get the link as written. This must not change anything for the Finance
 * team, who are the other recipient of every one of these events.
 */
export function resolveNotificationDestination(
  n: Pick<NotificationItem, 'deepLink' | 'customerId' | 'entityType' | 'entityId'>,
  access: NotificationAccess,
): NotificationDestination {
  const link = n.deepLink?.trim()
  if (!link) return { path: null }
  if (access.finance || !link.startsWith('/finance')) return { path: link }

  // Same page, same data, a route they're allowed to open — but only if they're in sales.
  // The queue's "All" filter is reachable from there, so nothing is hidden by the default.
  if (link.startsWith('/finance/follow-ups')) {
    if (access.sales) return { path: SALES_FOLLOW_UPS }
    return { path: null, message: "This one is on a Finance screen you don't have access to." }
  }

  // Invoice- and receivables-shaped links are really "go look at this customer's money".
  const customerId = customerIdOf(n)
  if (customerId) return { path: customerInvoicesPath(customerId, access.sales) }

  // Written before `customerId` existed on notifications. The entity IS an invoice, so the
  // customer is one lookup away — worth doing rather than telling the user a dead end.
  if (n.entityType === 'INVOICE' && n.entityId) {
    return { path: null, resolveCustomerFromInvoiceId: n.entityId }
  }

  return {
    path: null,
    message: "This one is on a Finance screen you don't have access to.",
  }
}
